import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | null): string {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function calculateCostFromTokens(inputTokens: number | null, outputTokens: number | null): number | null {
  if (inputTokens === null || outputTokens === null) return null;

  // Claude Sonnet 4.5 pricing (as of 2024)
  // Input: $3 per million tokens ($0.000003 per token)
  // Output: $15 per million tokens ($0.000015 per token)
  const inputCost = inputTokens * 0.000003;
  const outputCost = outputTokens * 0.000015;

  return inputCost + outputCost;
}

export function formatCost(cost: number | string | null | undefined): string {
  if (cost === null || cost === undefined) return 'N/A';

  // Convert string to number if needed
  const numericCost = typeof cost === 'string' ? parseFloat(cost) : cost;

  if (isNaN(numericCost)) return 'N/A';
  if (numericCost === 0) return '$0.00';

  // For very small costs, show up to 6 decimal places
  if (numericCost < 0.0001) {
    return `$${numericCost.toFixed(6)}`;
  }

  // For small costs, show 4 decimal places
  if (numericCost < 0.01) {
    return `$${numericCost.toFixed(4)}`;
  }

  // For larger costs, show 2 decimal places
  return `$${numericCost.toFixed(2)}`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return 'N/A';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }

  return `${minutes}m ${remainingSeconds}s`;
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}
